import React, { useCallback, useMemo, useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import ChevronDown from 'assets/svg/ChevronDown';
import { formatNumWithKMBAlt } from 'utils/helperMethods';

import type { ConstraintColumnType, ConstraintTableColumn } from '../../../../config/optimizePlanConfig';
import type { MetricFormat } from '../../../../config/metrics';
import { DEFAULT_METRIC_FORMAT } from '../../../../config/metrics';
import {
    TPO_OPTIMIZE_CONSTRAINTS_TABLE,
    TPO_CONSTRAINT_REMOVE_TEST_ID,
    tpoTableCellTestId,
    tpoTableColTestId,
    tpoTableExpandTestId,
    tpoTableRowTestId,
} from '../../../../config/testIds';
import type { ConstraintTableRow } from '../../utils/buildConstraintTableRows';
import { buildConstraintTableRows, collectExpandableKeys } from '../../utils/buildConstraintTableRows';
import type { ConstraintGroup, ConstraintInputValue, Level } from './types';
import AddLevelControl from './AddLevelControl';
import { NumberInputCell, RangeInputCell } from './ConstraintInputCells';

const SURFACE = 'optimize_constraints';

export interface ConstraintsTableLabels {
    addLevel: string;
    allLevelsAdded: string;
    removeLevel: string;
    removeAttribute: string;
    loading: string;
    error: string;
    empty: string;
}

interface Props {
    columns: ConstraintTableColumn[];
    groups: ConstraintGroup[];
    labels: ConstraintsTableLabels;
    onAddLevel: (constraintName: string, level: Level) => void;
    onRemoveLevel: (constraintName: string, level: Level) => void;
    onRemoveAttribute: (constraintName: string, level: Level, attributeName: string) => void;
    onInputChange: (
        constraintName: string,
        level: Level,
        attributeName: string,
        patch: Partial<ConstraintInputValue>,
        fallback: ConstraintInputValue,
    ) => void;
}

const TYPE_FLEX: Record<Exclude<ConstraintColumnType, 'hidden'>, number> = {
    label: 2,
    value: 1,
    range: 1,
    input: 1.5,
};

const ACTION_COL_WIDTH = 48;
const DEPTH_INDENT_PX = 20;

/**
 * Abbreviated (K/M/B) to fit the narrow columns, but with the precision and currency
 * adornments of the underlying metric, so a constraint cell reads the same as that
 * metric everywhere else.
 */
function formatValue(value: number | null | undefined, format: MetricFormat): string {
    if (value == null || isNaN(Number(value))) return '-';
    const { allowedDecimals, preUnit, postUnit } = format;
    return `${preUnit}${formatNumWithKMBAlt(Number(value), allowedDecimals, allowedDecimals)}${postUnit}`;
}

function getDisplayValue(column: ConstraintTableColumn, row: ConstraintTableRow): string {
    const format = row.format ?? DEFAULT_METRIC_FORMAT;
    const fields = row.fields;
    if (!fields) return '-';

    if (column.type === 'range') {
        const rangeData = fields[column.key];
        if (rangeData == null || typeof rangeData !== 'object') return '-';
        const { min, max } = rangeData as { min: number | null; max: number | null };
        if (min == null && max == null) return '-';
        return `${formatValue(min, format)} - ${formatValue(max, format)}`;
    }

    const val = fields[column.key] as number | string | null | undefined;
    if (val == null) return '-';
    if (typeof val === 'string') return val || '-';
    return formatValue(Number(val), format);
}

function computeColumnWidths(cols: ConstraintTableColumn[]): Map<string, string> {
    const visibleCols = cols.filter((c) => c.type !== 'hidden');
    const totalFlex = visibleCols.reduce(
        (sum, c) => sum + TYPE_FLEX[c.type as Exclude<ConstraintColumnType, 'hidden'>],
        0,
    );
    const widths = new Map<string, string>();
    for (const col of visibleCols) {
        widths.set(
            col.key,
            `${((TYPE_FLEX[col.type as Exclude<ConstraintColumnType, 'hidden'>] / totalFlex) * 100).toFixed(1)}%`,
        );
    }
    return widths;
}

const ConstraintsTable: React.FC<Props> = ({
    columns: columnConfig,
    groups,
    labels,
    onAddLevel,
    onRemoveLevel,
    onRemoveAttribute,
    onInputChange,
}) => {
    // Tracks explicit collapses instead of expansions, so a level added after the
    // first render opens on arrival rather than staying hidden behind a chevron.
    const [collapsedKeys, setCollapsedKeys] = useState<string[]>([]);

    const columnWidths = useMemo(() => computeColumnWidths(columnConfig), [columnConfig]);

    const dataSource = useMemo(() => buildConstraintTableRows(groups, labels), [groups, labels]);

    const expandedRowKeys = useMemo(() => {
        const collapsed = new Set(collapsedKeys);
        return collectExpandableKeys(dataSource).filter((key) => !collapsed.has(key));
    }, [dataSource, collapsedKeys]);

    const onToggleExpand = useCallback((key: string) => {
        setCollapsedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    }, []);

    const renderLabelCell = useCallback(
        (record: ConstraintTableRow) => {
            const isExpandable = (record.children?.length ?? 0) > 0;
            const isExpanded = isExpandable && expandedRowKeys.includes(record.rowKey);

            if (record.kind === 'add_level') {
                return (
                    <div style={{ paddingLeft: record.depth * DEPTH_INDENT_PX }}>
                        <AddLevelControl
                            constraintName={record.constraintName}
                            options={record.addableLevels ?? []}
                            onSelect={onAddLevel}
                            addLabel={labels.addLevel}
                            exhaustedLabel={labels.allLevelsAdded}
                        />
                    </div>
                );
            }

            return (
                <div className="flex items-center gap-1.5" style={{ paddingLeft: record.depth * DEPTH_INDENT_PX }}>
                    {isExpandable ? (
                        <span
                            className="cursor-pointer flex items-center p-1 text-[#595959] hover:text-[#44338a] transition-colors"
                            data-testid={tpoTableExpandTestId(SURFACE, record.rowKey)}
                            data-state-expanded={isExpanded ? 'true' : undefined}
                            onClick={() => onToggleExpand(record.rowKey)}
                        >
                            <ChevronDown className={isExpanded ? 'rotate-180' : 'rotate-0'} size={12} />
                        </span>
                    ) : (
                        <span className="w-[20px] shrink-0" />
                    )}
                    <span className={record.kind === 'status' ? 'text-[rgba(0,0,0,0.45)] italic' : undefined}>
                        {record.label}
                    </span>
                </div>
            );
        },
        [expandedRowKeys, labels.addLevel, labels.allLevelsAdded, onAddLevel, onToggleExpand],
    );

    const renderInputCell = useCallback(
        (record: ConstraintTableRow, columnKey: string) => {
            if (record.kind !== 'attribute' || !record.input) {
                return <span className="text-[#bfbfbf] select-none">—</span>;
            }
            const { constraintName, level, attributeName, input } = record;
            const cellTestId = tpoTableCellTestId(SURFACE, record.rowKey, columnKey);

            if (record.inputType === 'number') {
                return (
                    <NumberInputCell
                        value={input.single}
                        invalid={record.inputInvalid ?? false}
                        onChange={(val) =>
                            onInputChange(constraintName, level!, attributeName!, { single: val }, input)
                        }
                        testId={cellTestId}
                    />
                );
            }
            return (
                <RangeInputCell
                    minVal={input.min}
                    maxVal={input.max}
                    invalid={record.inputInvalid ?? false}
                    onMinChange={(val) => onInputChange(constraintName, level!, attributeName!, { min: val }, input)}
                    onMaxChange={(val) => onInputChange(constraintName, level!, attributeName!, { max: val }, input)}
                    testId={cellTestId}
                />
            );
        },
        [onInputChange],
    );

    const renderActionCell = useCallback(
        (record: ConstraintTableRow) => {
            if (record.kind !== 'level' && record.kind !== 'attribute') return null;
            const isLevel = record.kind === 'level';
            return (
                <button
                    type="button"
                    aria-label={isLevel ? labels.removeLevel : labels.removeAttribute}
                    data-testid={TPO_CONSTRAINT_REMOVE_TEST_ID(tpoTableRowTestId(SURFACE, record.rowKey))}
                    onClick={() =>
                        isLevel
                            ? onRemoveLevel(record.constraintName, record.level!)
                            : onRemoveAttribute(record.constraintName, record.level!, record.attributeName!)
                    }
                    className="flex items-center justify-center p-1 text-[#595959] hover:text-[#ff4d4f] transition-colors"
                >
                    <DeleteOutlined />
                </button>
            );
        },
        [labels.removeLevel, labels.removeAttribute, onRemoveLevel, onRemoveAttribute],
    );

    const tableColumns = useMemo<ColumnsType<ConstraintTableRow>>(
        () => [
            ...columnConfig
                .filter((col) => col.type !== 'hidden')
                .map((col) => ({
                    title: col.label,
                    dataIndex: col.key,
                    key: col.key,
                    width: columnWidths.get(col.key),
                    onHeaderCell: () =>
                        ({
                            'data-testid': tpoTableColTestId(SURFACE, col.key),
                        } as React.ThHTMLAttributes<HTMLTableCellElement>),
                    onCell: (record: ConstraintTableRow) =>
                        ({
                            'data-testid': tpoTableCellTestId(SURFACE, record.rowKey, col.key),
                            className: record.kind === 'attribute' ? 'text-[#595959]' : 'text-[#18181b]',
                        } as React.TdHTMLAttributes<HTMLTableCellElement>),
                    render: (_: unknown, record: ConstraintTableRow) => {
                        if (col.type === 'label') return renderLabelCell(record);
                        if (record.kind !== 'attribute') return null;
                        if (col.type === 'input') return renderInputCell(record, col.key);
                        return <span className="text-sm">{getDisplayValue(col, record)}</span>;
                    },
                })),
            {
                title: '',
                key: '_actions',
                width: ACTION_COL_WIDTH,
                render: (_: unknown, record: ConstraintTableRow) => renderActionCell(record),
            },
        ],
        [columnConfig, columnWidths, renderLabelCell, renderInputCell, renderActionCell],
    );

    return (
        <div className="h-full flex flex-col min-h-0 overflow-hidden rounded-lg border border-[rgba(240,240,240,1)] [&_.ant-table-wrapper]:h-full [&_.ant-table-wrapper_.ant-spin-nested-loading]:h-full [&_.ant-table-wrapper_.ant-spin-container]:h-full [&_.ant-table-wrapper_.ant-table]:h-full [&_.ant-table-wrapper_.ant-table-container]:h-full [&_.ant-table-wrapper_.ant-table-container]:flex [&_.ant-table-wrapper_.ant-table-container]:flex-col [&_.ant-table-wrapper_.ant-table-header]:shrink-0 [&_.ant-table-wrapper_.ant-table-body]:flex-1 [&_.ant-table-wrapper_.ant-table-body]:min-h-0 [&_.ant-table-wrapper_.ant-table-body]:!overflow-y-auto [&_.ant-table-tbody>tr:last-child>td]:border-b-0">
            <Table<ConstraintTableRow>
                columns={tableColumns}
                dataSource={dataSource}
                rowKey="rowKey"
                pagination={false}
                size="small"
                data-testid={TPO_OPTIMIZE_CONSTRAINTS_TABLE}
                onRow={(record) =>
                    ({
                        'data-testid': tpoTableRowTestId(SURFACE, record.rowKey),
                        'data-row-kind': record.kind,
                    } as React.HTMLAttributes<HTMLTableRowElement>)
                }
                sticky
                tableLayout="fixed"
                scroll={{ y: '100%' }}
                expandable={{
                    expandedRowKeys,
                    expandIcon: () => null,
                    indentSize: 0,
                }}
            />
        </div>
    );
};

ConstraintsTable.displayName = 'ConstraintsTable';
export default React.memo(ConstraintsTable);
